import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAsyncData } from "@/hooks/useAsyncData";
import {
  listAll,
  createItem,
  updateItem,
  removeItem,
  uploadImage,
} from "@/lib/adminRepository";
import { COLLECTIONS } from "@/pages/admin/collections.config";
import { ADMIN } from "@/constants/testIds";
import { Plus, Trash2, Pencil, X, Upload } from "lucide-react";

const tsToLocal = (v) => {
  if (!v) return "";
  const d = v.toDate ? v.toDate() : new Date(v);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function blankItem(cfg) {
  const o = {};
  cfg.fields.forEach((f) => {
    o[f.name] = f.default ?? (f.type === "imageList" ? [] : f.type === "number" ? "" : "");
  });
  return o;
}

function FieldInput({ field, value, onChange }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file, cb) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      cb(url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Upload failed");
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  switch (field.type) {
    case "textarea":
      return (
        <textarea rows={field.name === "body" ? 8 : 3} className="editorial-input resize-y" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      );
    case "number":
      return <input type="number" className="editorial-input" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />;
    case "select":
      return (
        <select className="editorial-input" value={value || field.default || ""} onChange={(e) => onChange(e.target.value)}>
          {field.options.map((o) => (<option key={o} value={o}>{o}</option>))}
        </select>
      );
    case "datetime":
      return <input type="datetime-local" className="editorial-input" value={tsToLocal(value)} onChange={(e) => onChange(e.target.value ? new Date(e.target.value) : "")} />;
    case "image":
      return (
        <div className="flex items-center gap-3">
          <input type="text" className="editorial-input flex-1" placeholder="https://… or upload" value={value || ""} onChange={(e) => onChange(e.target.value)} />
          <label className="inline-flex items-center gap-1 text-xs text-bronze cursor-pointer border border-bronze/40 px-3 py-2 hover:bg-bronze hover:text-cream">
            <Upload size={14} /> {uploading ? "…" : "Upload"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0], onChange)} />
          </label>
          {value && <img src={value} alt="" className="h-10 w-10 object-cover border border-bronze/20" />}
        </div>
      );
    case "imageList": {
      const arr = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-2">
          <textarea rows={3} className="editorial-input resize-y" placeholder="One image URL per line" value={arr.join("\n")} onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />
          <label className="inline-flex items-center gap-1 text-xs text-bronze cursor-pointer border border-bronze/40 px-3 py-2 hover:bg-bronze hover:text-cream w-fit">
            <Upload size={14} /> {uploading ? "…" : "Add image"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0], (url) => onChange([...arr, url]))} />
          </label>
        </div>
      );
    }
    default:
      return <input type="text" className="editorial-input" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
}

export default function CollectionManager() {
  const { key } = useParams();
  const cfg = COLLECTIONS[key];
  const { data: items, loading, error, reload } = useAsyncData(() => listAll(cfg.coll), [key]);
  const [editing, setEditing] = useState(null); // item being edited (or new)
  const [saving, setSaving] = useState(false);

  if (!cfg) return <div className="p-8">Unknown collection.</div>;

  const startNew = () => setEditing(blankItem(cfg));
  const startEdit = (item) => setEditing({ ...item });

  const save = async () => {
    // required-field validation
    for (const f of cfg.fields) {
      if (f.required && !String(editing[f.name] ?? "").trim()) {
        toast.error(`${f.label} is required.`);
        return;
      }
    }
    setSaving(true);
    try {
      const { id, ...data } = editing;
      if (id) {
        await updateItem(cfg.coll, id, data);
        toast.success("Saved");
      } else {
        const newId = cfg.idField ? String(data[cfg.idField]).trim() : undefined;
        await createItem(cfg.coll, data, newId);
        toast.success("Created");
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error("Save failed: " + (e?.message || "error"));
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const del = async (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${item[cfg.titleField] || item.id}"? This cannot be undone.`)) return;
    try {
      await removeItem(cfg.coll, item.id);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const togglePublish = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    await updateItem(cfg.coll, item.id, { status: next });
    reload();
  };

  return (
    <div data-testid={`admin-collection-${key}`}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl text-ink">{cfg.label}</h1>
        <button onClick={startNew} data-testid={ADMIN.newButton} className="btn-primary inline-flex items-center gap-2">
          <Plus size={16} /> New
        </button>
      </div>

      {loading && <p className="text-olive">Loading…</p>}
      {error && <p className="text-red-700">Failed to load. <button className="underline" onClick={reload}>Retry</button></p>}

      {!loading && !error && (
        <table className="w-full text-sm border border-bronze/20 bg-cream" data-testid={ADMIN.list}>
          <thead className="bg-beige/60 text-left text-[0.65rem] uppercase tracking-wider text-olive">
            <tr>
              <th className="p-3">{cfg.label.replace(/s$/, "")}</th>
              <th className="p-3 w-28">Status</th>
              <th className="p-3 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-bronze/10" data-testid={`admin-row-${item.id}`}>
                <td className="p-3 font-medium text-ink">{item[cfg.titleField] || item.id}</td>
                <td className="p-3">
                  <button
                    onClick={() => togglePublish(item)}
                    data-testid={ADMIN.publishToggle}
                    className={`px-2 py-1 text-[0.6rem] uppercase tracking-wider ${item.status === "published" ? "bg-bronze text-cream" : "bg-taupe/40 text-ink"}`}
                  >
                    {item.status || "draft"}
                  </button>
                </td>
                <td className="p-3 flex gap-3">
                  <button onClick={() => startEdit(item)} className="text-bronze hover:text-bronze_dark" title="Edit"><Pencil size={16} /></button>
                  <button onClick={() => del(item)} data-testid={ADMIN.deleteButton} className="text-red-600 hover:text-red-800" title="Delete"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={3} className="p-6 text-center text-olive">Nothing yet — click “New”.</td></tr>
            )}
          </tbody>
        </table>
      )}

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex justify-end" onClick={() => !saving && setEditing(null)}>
          <div className="w-full max-w-xl h-full bg-cream overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid={ADMIN.editForm}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-ink">{editing.id ? "Edit" : "New"} {cfg.label.replace(/s$/, "")}</h2>
              <button onClick={() => setEditing(null)} className="text-olive hover:text-ink"><X size={20} /></button>
            </div>
            <div className="space-y-5">
              {cfg.fields.map((f) => (
                <label key={f.name} className="block">
                  <span className="editorial-label">{f.label}{f.required && " *"}</span>
                  <FieldInput field={f} value={editing[f.name]} onChange={(v) => setEditing((prev) => ({ ...prev, [f.name]: v }))} />
                </label>
              ))}
            </div>
            <div className="mt-8 flex gap-3">
              <button onClick={save} disabled={saving} data-testid={ADMIN.saveButton} className="btn-primary disabled:opacity-60">
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={() => setEditing(null)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
